import * as React from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
    icon?: LucideIcon;
    title: string;
    description?: React.ReactNode;
    /** Buttons or links that move the user forward */
    action?: React.ReactNode;
    /** `card` draws its own surface; `inline` sits inside an existing card/table */
    variant?: 'inline' | 'card';
    size?: 'sm' | 'md';
    className?: string;
}

/** Shown when a list, table or panel has nothing to display. Always say why and what to do next. */
export const EmptyState: React.FC<EmptyStateProps> = ({
    icon: Icon = Inbox, title, description, action, variant = 'inline', size = 'md', className,
}) => (
    <div
        className={cn(
            'flex flex-col items-center justify-center text-center',
            size === 'md' ? 'gap-4 px-6 py-14' : 'gap-3 px-4 py-8',
            variant === 'card' && 'rounded-xl border border-dashed bg-card',
            className
        )}
    >
        <div className={cn('flex items-center justify-center rounded-full bg-muted text-muted-foreground', size === 'md' ? 'h-14 w-14' : 'h-11 w-11')}>
            <Icon className={size === 'md' ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden />
        </div>
        <div className="max-w-sm space-y-1">
            <p className="text-subheading text-foreground">{title}</p>
            {description && <p className="text-body-sm text-muted-foreground">{description}</p>}
        </div>
        {action && <div className="flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
);
